import React from 'react';
import { Layout, Button } from 'antd';
import { Outlet } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import Header from './Header';
import Sidebar, { useSidebarContext, SidebarProvider } from './Sidebar';
import { useTheme } from '../../contexts/ThemeContext';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar/styles';

const { Content } = Layout;

// Header 高度常量
const HEADER_HEIGHT = 64;

/**
 * 布局内容组件
 * 需要在 SidebarProvider 内部使用
 */
const LayoutContent = () => {
  const { collapsed, isMobile, openDrawer } = useSidebarContext();
  const { isDark } = useTheme();
  
  // 计算内容区域的左边距
  const marginLeft = isMobile ? 0 : (collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH);
  
  // 内容区域样式
  // 用固定高度（而非 minHeight）把滚动收敛到内容区自己：
  // 页面比视口高时如果由 window 滚动，打开弹框时 antd 的滚动锁定会给 body 减掉一个滚动条宽度
  // 来补偿滚动条消失，但固定定位的 Header 是按视口定宽的，于是弹框一开 Header 就横向跳一下。
  // 内容区自己滚动后 window 永不出现滚动条，这份补偿恒为 0，全站弹框都不再位移。
  const contentStyle = {
    marginTop: HEADER_HEIGHT, // 为固定 Header 留出空间
    padding: 24,
    background: isDark ? 'var(--color-bg-container, #141414)' : 'var(--color-bg-container, #fff)',
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    maxHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
    overflow: 'auto',
    overscrollBehavior: 'contain',
    transition: 'background-color var(--transition-normal, 250ms)',
  };
  
  // 布局样式
  const layoutStyle = {
    marginLeft,
    minHeight: '100vh',
    transition: 'margin-left 0.2s, background-color var(--transition-normal, 250ms)',
    background: isDark ? 'var(--color-bg-base, #0a0a0a)' : 'var(--color-bg-base, #f5f7fa)',
  };
  
  // Header 容器样式 - 固定定位
  const headerWrapperStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    left: marginLeft,
    zIndex: 99,
    transition: 'left 0.2s',
  };
  
  return (
    <Layout style={layoutStyle}>
      <div style={headerWrapperStyle}>
        <Header 
          extra={
            isMobile && (
              <Button 
                type="text" 
                icon={<MenuOutlined />} 
                onClick={openDrawer}
                style={{ marginRight: 16 }}
              />
            )
          }
        />
      </div>
      <Content style={contentStyle} className="fade-in">
        <Outlet />
      </Content>
    </Layout>
  );
};

/**
 * 应用布局组件
 */
const AppLayout = () => {
  const { isDark } = useTheme();
  
  return (
    <SidebarProvider>
      <Layout style={{ 
        minHeight: '100vh',
        background: isDark ? 'var(--color-bg-base, #0a0a0a)' : 'var(--color-bg-base, #f5f7fa)',
        transition: 'background-color var(--transition-normal, 250ms)',
      }}>
        <Sidebar />
        <LayoutContent />
      </Layout>
    </SidebarProvider>
  );
};

export default AppLayout;
